import { Card, H3, Separator } from "@dune/ui";
import { ClipboardList } from "lucide-react";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useUnlinkFileFromConstructionSiteSubmission } from "../../../api/mutations/constructionSiteSubmission.mutations";
import { useImportEmailAttachmentsConstructionSite } from "../../../api/mutations/mail.mutations";
import { useGetConstructionSiteSubmissionOperation } from "../../../api/queries/constructionSiteSubmission.queries";
import { useGetConstructionSiteReceptionOperationFiles } from "../../../api/queries/constructionSiteReception.queries";
import { useConstructionSiteDetailsForm } from "../../../component/brokers/constructionSites/useConstructionSiteDetailsForm";
import { OperationDocuments } from "../../../component/submission-operation/OperationDocumentsTable";
import { AdministrativeAuthorizationForm } from "../../../component/submission-operation/administrativeAuthorizationForm/AdministrativeAuthorizationForm";
import { useAdministrativeAuthorizationForm } from "../../../component/submission-operation/administrativeAuthorizationForm/useAdministrativeAuthorizationForm";
import { useEntityPanelSync } from "../../../hooks/useEntityPanelSync";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import { ConstructionSiteEmailComposerPanel } from "../components/panels/ConstructionSiteEmailComposerPanel";
import ConstructionSiteTaskDetailsPanel from "../components/panels/ConstructionSiteTaskDetailsPanel";
import { MainDetailsOperation } from "../components/tabs/operation";
import ReceptionDatesDetailsContainer from "./ReceptionDatesDetailsContainer";
import { generateBrokerEmailWithFallback } from "../../../utils/emails.utils";
import { useMe } from "../../../api/queries/users.queries";

export const ReceptionOperation = () => {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const {
		data: submissionWithOperationDetails,
		isLoading,
		error,
	} = useGetConstructionSiteSubmissionOperation(Number(constructionSiteId));

	const { data: receptionOperationFiles } =
		useGetConstructionSiteReceptionOperationFiles(Number(constructionSiteId));

	const { form: constructionSiteDetailsForm } = useConstructionSiteDetailsForm(
		submissionWithOperationDetails?.constructionSite,
	);

	const { form: administrativeAuthorizationForm } =
		useAdministrativeAuthorizationForm(submissionWithOperationDetails);

	const { mutate: importEmailAttachments } =
		useImportEmailAttachmentsConstructionSite(Number(constructionSiteId));

	const { mutate: unlinkFile } = useUnlinkFileFromConstructionSiteSubmission(
		Number(constructionSiteId),
	);

	const panelComponents = useMemo(
		() => ({
			TaskDetails: ConstructionSiteTaskDetailsPanel,
			EmailComposer: ConstructionSiteEmailComposerPanel,
		}),
		[],
	);

	const { me } = useMe();

	const isDune = useMemo(() => me?.role === "DUNE", [me]);

	const panelEmailConfig = useMemo(
		() => ({
			getReplyToEmail: (entityId: number, emailRoutingUuid?: string | null) => {
				if (emailRoutingUuid) {
					return generateBrokerEmailWithFallback(emailRoutingUuid, entityId);
				}
				return `chantier-${entityId}@dune.com`;
			},
			importEmailAttachmentsHandler: (emailAttachmentId: number) => {
				importEmailAttachments({ emailAttachmentId });
			},
		}),
		[importEmailAttachments],
	);

	const documentConfig = useMemo(
		() => ({
			onDelete: (documentId: string) => {
				unlinkFile(documentId);
			},
		}),
		[unlinkFile],
	);

	useEntityPanelSync({
		entityType: "CONSTRUCTION_SITE",
		entityId: Number(constructionSiteId),
		communications: [],
		tasks: [],
		emailRoutingUuid: null,
		components: panelComponents,
		emailConfig: panelEmailConfig,
		documentConfig: documentConfig,
	});

	if (isLoading) return <div>Chargement des détails de l'opération...</div>;
	if (error)
		return <div>Erreur lors du chargement des détails de l'opération.</div>;

	if (!submissionWithOperationDetails)
		return (
			<div className="text-center py-8">
				<ClipboardList className="h-16 w-16 text-gray-400 mx-auto mb-4" />
				<h3 className="text-lg font-semibold text-gray-600 mb-2">
					Aucune donnée d'opération pour la réception
				</h3>
				<p className="text-gray-500 mb-4">
					Les détails de l'opération seront disponibles une fois la réception
					effectuée.
				</p>
			</div>
		);

	return (
		<div>
			<FormProvider {...constructionSiteDetailsForm}>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<div className="flex flex-row justify-between items-center">
							<H3>INFORMATIONS SUR L'OPÉRATION</H3>
						</div>
						<MainDetailsOperation
							isDisabled
							noShadow
							showConstructionCost
							isReception
						/>
					</div>
				</div>
			</FormProvider>

			<Separator className="my-8" />

			<FormProvider {...administrativeAuthorizationForm}>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<H3>AUTORISATION ADMINISTRATIVE</H3>
						<Card noShadow className="p-6">
							<AdministrativeAuthorizationForm disabled />
						</Card>
					</div>
					<div className="flex-1">
						<OperationDocuments
							constructionSiteId={Number(constructionSiteId)}
							section="ADMINISTRATIVE_AUTHORIZATION"
							submissionOperationDetails={submissionWithOperationDetails}
							receptionOperationFiles={
								receptionOperationFiles?.filesOnConstructionSiteReception
							}
							disabled={!isDune}
							isReception
						/>
					</div>
				</div>
			</FormProvider>

			<Separator className="my-8" />

			<FormProvider {...constructionSiteDetailsForm}>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<div className="flex flex-row justify-between items-center">
							<H3>DATES DE L'OPÉRATION</H3>
						</div>
						<ReceptionDatesDetailsContainer
							receptionDate={
								submissionWithOperationDetails.constructionSite
									.constructionSiteDetails?.receptionDate ?? undefined
							}
							cityHallStartDate={
								submissionWithOperationDetails.constructionSite
									.constructionSiteDetails?.cityHallStartDate ?? undefined
							}
							startDate={
								submissionWithOperationDetails.constructionSite
									.constructionSiteDetails?.startDate ?? undefined
							}
							noShadow
						/>
					</div>
					<div className="flex-1">
						<OperationDocuments
							constructionSiteId={Number(constructionSiteId)}
							section="OPERATION_DATES"
							submissionOperationDetails={submissionWithOperationDetails}
							receptionOperationFiles={
								receptionOperationFiles?.filesOnConstructionSiteReception
							}
							isReception
							disabled={!isDune}
						/>
					</div>
				</div>
			</FormProvider>
		</div>
	);
};
